/*
 * FILE LOCATION: frontend/src/services/auth.js
 * REPLACE: Yes, replace the entire existing file
 *
 * CRITICAL FIXES:
 * 1. register() now SAVES the token (like login does)
 * 2. register() now FETCHES and returns user data
 * 3. Consistent token handling across login and registration
 *
 * Authentication service — handles login, registration, and user retrieval.
 * Compatible with FastAPI OAuth2 and JSON endpoints.
 */

import api, { tokenStorage } from './api';

export const authService = {
  /**
   * Login with email and password.
   * FastAPI expects form-encoded data for OAuth2 login.
   *
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {boolean} remember - Whether to persist token
   * @returns {Promise<{user, access_token, token_type}>}
   */
  async login(email, password, remember = true) {
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await api.post('/api/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const { access_token, token_type } = response.data;

      // Save token to storage
      if (access_token) {
        tokenStorage.set(access_token, remember);
      }

      // Fetch user data with the new token
      const userResponse = await api.get('/api/auth/me');
      const user = userResponse.data;

      return { user, access_token, token_type };
    } catch (error) {
      console.error('❌ Login failed:', error?.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Register a new account.
   * Backend expects JSON: { full_name, email, password }
   * Backend returns Token: { access_token, token_type, expires_in }
   *
   * ✅ CRITICAL FIX: Now saves token and fetches user (like login does)
   *
   * @param {Object} userData - Registration data
   * @param {string} userData.full_name - User's full name
   * @param {string} userData.email - User's email
   * @param {string} userData.password - User's password
   * @returns {Promise<{user, access_token, token_type}>}
   */
  async register({ full_name, email, password }) {
    try {
      const payload = { full_name, email, password };
      console.log('📤 Registering user with payload:', payload);

      // Register and get token
      const response = await api.post('/api/auth/register', payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      console.log('✅ Registration successful:', response.data);

      const { access_token, token_type, expires_in } = response.data;

      // ✅ CRITICAL FIX: Save the token (was missing before!)
      if (access_token) {
        tokenStorage.set(access_token, true); // Always remember for new registrations
        console.log('✅ Token saved to storage');
      } else {
        console.error('❌ No access token in registration response!');
        throw new Error('Registration succeeded but no token received');
      }

      // ✅ CRITICAL FIX: Fetch user data with the new token (was missing before!)
      const userResponse = await api.get('/api/auth/me');
      const user = userResponse.data;
      console.log('✅ User data fetched:', user);

      return { user, access_token, token_type, expires_in };
    } catch (error) {
      console.error('❌ Registration error:', error?.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Retrieve the currently authenticated user (requires valid token).
   *
   * @returns {Promise<Object>} User data
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch (error) {
      console.error('⚠️ Failed to fetch current user:', error?.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Log out — clears token storage.
   */
  logout() {
    console.log('👋 Logging out user');
    tokenStorage.clear();
  },
};